import { Component, OnInit, inject } from '@angular/core';

import { ReactiveFormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormlyModule } from '@ngx-formly/core';
import { DocumentTypeField, ListFilter } from '@parashift/shared/models';
import { ModalService } from '@parashift/shared/components';
import { PageNumberPipe } from '@parashift/shared/pipes';
import { PreventEnterDirective } from '@parashift/shared/directives';
import { BusinessSectorsRegister, CurrentTenantRegister, DocumentTypeFieldService, ErrorResponse, LocalesRegister, Logger, NotificationService, TransformerService, VerifierService } from '@parashift/shared/services';
import { EditFieldsetFieldForm } from './edit-fieldset-field.form';

@Component({
  selector: 'pdc-edit-fieldset-field-modal',
  templateUrl: './edit-fieldset-field-modal.component.html',
  imports: [ReactiveFormsModule, PreventEnterDirective, FormlyModule]
})
export class EditFieldsetFieldModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);
  private documentTypeFieldService = inject(DocumentTypeFieldService);
  private verifierService = inject(VerifierService);
  private transformerService = inject(TransformerService);
  localesRegister = inject(LocalesRegister);
  businessSectorsRegister = inject(BusinessSectorsRegister);
  currentTenantRegister = inject(CurrentTenantRegister);
  modalService = inject(ModalService);
  private notificationService = inject(NotificationService);
  private logger = inject(Logger);

  tenant_id: number;
  id: string;
  documentTypeField: DocumentTypeField;
  editFieldsetFieldForm: EditFieldsetFieldForm;
  state: boolean;
  data: { field_id?: string; } = {};

  ngOnInit() {
    if (this.data.field_id && this.data.field_id !== 'new') {
      this.id = this.data.field_id;
      this.getDocumentTypeField();
    } else {
      this.id = 'new';
      this.initDocumentTypeField();
    }
  }

  saveDocumentTypeField(model: DocumentTypeField['plainModel']) {
    if (this.editFieldsetFieldForm.form.valid) {
      this.documentTypeField.plainModel = { ...model, optional: !model.optional };
      this.documentTypeField.page_number = PageNumberPipe.untransform(this.documentTypeField.page_number);

      this.documentTypeField.extraction_type_id = null;
      this.editFieldsetFieldForm.form.disable();

      this.documentTypeFieldService.saveRecord(this.documentTypeField, this.tenant_id).subscribe({
        next: (documentTypeField: DocumentTypeField) => {
          this.notificationService.success({
            description: $localize `:@@components.edit_fieldset_field_modal.document_type_field_saved_description:The Document Type field has been successfully saved.`,
            title: $localize `:@@components.edit_fieldset_field_modal.document_type_field_saved_title:Document type field saved`
          });
          this.state = true;
          this.bsModalRef.content.model = documentTypeField;
          this.bsModalRef.hide();

        },
        error: (error: ErrorResponse) => {
          this.editFieldsetFieldForm.form.enable();
          this.logger.log('Save Document Type Field Error', error);
          this.notificationService.formattedErrorResponse(error);
        }
      });
    }
  }

  close() {
    this.bsModalRef.hide();
  }

  private getDocumentTypeField() {
    const listFilter = new ListFilter({ include: 'extraction_type' });
    this.documentTypeFieldService.findRecord(this.id, listFilter, this.tenant_id).subscribe({
      next: (documentTypeField: DocumentTypeField) => {
        this.setDocumentTypeFieldAndForm(documentTypeField);
      },
      error: (error: ErrorResponse) => {
        this.logger.log('edit-fieldset-field-modal-component', 'DOCUMENT TYPE FIELD Error', error);
      }
    });
  }

  private initDocumentTypeField() {
    const documentTypeField = new DocumentTypeField({
      tenant_id: this.tenant_id,
      extraction_type_id: '',
      visibility: 'tenant',
      optional: true
    });

    this.setDocumentTypeFieldAndForm(documentTypeField);
  }

  private setDocumentTypeFieldAndForm(documentTypeField: DocumentTypeField) {
    this.documentTypeField = documentTypeField;
    const plainModel = this.documentTypeField.plainModel;
    plainModel.page_number = PageNumberPipe.transform(plainModel.page_number);
    plainModel.optional = !documentTypeField.optional;

    const listFilter = new ListFilter({
      page: { number: 1, size: 200},
      sorting: { field: 'title', direction: '' }
    });

    const multiRequests = forkJoin([
      this.transformerService.findAll(listFilter),
      this.verifierService.findAll(listFilter)
    ]);

    multiRequests.subscribe({
      next: (response) => {
        this.editFieldsetFieldForm = new EditFieldsetFieldForm(
          plainModel,
          {},
          response[0].getModels(),
          response[1].getModels(),
          this.localesRegister,
          this.businessSectorsRegister,
          this.currentTenantRegister
        );
      },
      error: (error: ErrorResponse) => {
        this.logger.log('MULTI REQUESTS TRANSFORMERS & VERIFIERS Error', error);
        this.notificationService.formattedErrorResponse(error);
      }
    });
  }
}
